import { useRef, useState, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { ChromePicker } from 'react-color';
import { WebChatColorProps, WebChatBannerSettings } from "@42maru/webchat";


const PICKER_WIDTH = 225;

const convertColorToHex = (color?: string) => {
    if (!color) {
        return color;
    }
    switch (color.toUpperCase()) {
        case "WHITE":
            return "#ffffff";
        case "GRAY":
            return "#808080";
        default:
            return color;
    }
};

type WebchatColorPickerProps<T extends WebChatColorProps | WebChatBannerSettings> = {
    "aria-label"?: string;
    colorKey: keyof T;
    colors: T;
    onChange: (color: string, colorKey: keyof T) => void;
}

const WebchatColorPicker = <T extends WebChatColorProps | WebChatBannerSettings>(props: WebchatColorPickerProps<T>) => {
    const { "aria-label": ariaLabel, colorKey, colors, onChange } = props;
    const color = (colors as any)[colorKey] as string | undefined;

    const colorValueId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const [pickerInputElement, setPickerInputElement] = useState<HTMLInputElement | null>(null);

    const getPopoverPosition = () => {
        if (!buttonRef.current) {
            return { top: 0, left: 0 };
        }
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const containerRect = buttonRef.current.parentElement?.getBoundingClientRect();
        
        if (!containerRect) {
            return { top: 0, left: 0 };
        }
        return {
            width: `${PICKER_WIDTH}px`,
            top: buttonRect.bottom + window.scrollY + 12,
            left: containerRect.left + (containerRect.width / 2) - (PICKER_WIDTH / 2) + window.scrollX,
        };
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case "Escape":
                case "Enter":
                    event.preventDefault();
                    setIsOpen(false);
                    break;
                case " ":
                    if (!pickerInputElement || pickerInputElement !== event.target) {
                        event.preventDefault();
                        setIsOpen(false);
                    }
                    break;
                case "Tab":
                    event.preventDefault();
                    if (!pickerInputElement || pickerInputElement === event.target) {
                        setIsOpen(false);
                        buttonRef.current?.focus();
                    } else {
                        pickerInputElement?.focus();
                    }
                    break;
                default:
                    break;
            }
        };

        const handleClickOutside = (event: MouseEvent) => {
            if (
                buttonRef.current &&
                pickerRef.current &&
                !buttonRef.current.contains(event.target as Node) &&
                !pickerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('keydown', handleKeyDown);
            requestAnimationFrame(() => setIsAnimating(true));
        } else {
            setIsAnimating(false);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, pickerInputElement]);

    return (
        <>
            <div role="group"
                 aria-label={ariaLabel}
                 className="relative w-fit py-1.5 pl-2.5 pr-3.5 flex items-center gap-2.5 border border-black-200 rounded-full"
            >
                <button ref={buttonRef}
                        type="button"
                        aria-label={ariaLabel}
                        aria-haspopup="dialog"
                        aria-expanded={isOpen}
                        aria-describedby={colorValueId}
                        className="color-circle border border-black-200"
                        style={{ background: convertColorToHex(color) }}
                        onClick={() => setIsOpen(!isOpen)}
                />
                <div id={colorValueId}
                     data-testid="webchat-color-picker-value"
                     className="w-[60px] text-black-500 text-[14px] tabular-nums"
                >
                    {convertColorToHex(color)?.toUpperCase()}
                </div>
            </div>
            {(isOpen || isAnimating) && createPortal(
                <div ref={pickerRef}
                     role="dialog"
                     aria-label={ariaLabel}
                     className={classNames(
                         "fixed border-2 border-black-200 rounded-lg bg-white shadow-lg z-50",
                         "transition-all duration-200 ease-in-out transform",
                         isOpen && isAnimating
                             ? "opacity-100 scale-100"
                             : "opacity-0 scale-95 pointer-events-none"
                     )}
                     style={getPopoverPosition()}
                     onTransitionEnd={() => {
                         if (!isOpen) {
                            setIsAnimating(false);
                         } else {
                            setPickerInputElement(pickerRef.current?.querySelector<HTMLInputElement>('input[id^="rc-editable-input-"]') || null);
                        }
                     }}
                >
                    <ChromePicker color={color}
                                  styles={{
                                       default: {
                                           picker: {
                                               width: `${PICKER_WIDTH}px`,
                                           },
                                       },
                                   }}
                                  onChange={({ hex }) => onChange(hex, colorKey as keyof T)}
                    />
                </div>,
                document.body
            )}
        </>
    );
};

export default WebchatColorPicker;